import React, { useContext, useEffect, useState } from "react";
import { createOrUpdateVersion, deleteVersion, getVersions } from "../services/adminServices";
import { AppContext } from "../context/AppContext";
import { toast } from "react-toastify";
import moment from "moment";
import { CONFIG } from "../config/general";

export const VersionManager = () => {
  const { login } = useContext(AppContext);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    version: "",
    type: "",
    warnDate: "",
    expiryDate: "",
  });
  const [uploadProgress, setUploadProgress] = useState(0);

  // Pagination (basic example)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      const res = await getVersions(login.token);
      setVersions(res.data || []);
    } catch (error) {
      console.error("Error fetching versions:", error);
      toast.error("Failed to fetch versions");
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(versions.length / itemsPerPage);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages || totalPages === 0;
  const paginatedVersions = versions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goPrev = () => {
    if (!isFirstPage) setCurrentPage(currentPage - 1);
  };
  const goNext = () => {
    if (!isLastPage) setCurrentPage(currentPage + 1);
  };

  // Open Add/Edit modal
  const openVersionModal = (version = null) => {
    if (version) {
      setForm({
        _id: version._id,
        version: version.version || "",
        type: version.type || "",
        warnDate: version.warnDate ? version.warnDate.substring(0, 10) : "", // format date YYYY-MM-DD
        expiryDate: version.expiryDate ? version.expiryDate.substring(0, 10) : "",
        windowsFile: version.windowsFile || "",
        linuxFile: version.linuxFile || "",
        macFile: version.macFile || "",
      });
    } else {
      setForm({
        version: "",
        type: "",
        warnDate: "",
        expiryDate: "",
        windowsFile: "",
        linuxFile: "",
        macFile: "",
      });
    }
    setShowModal(true);
  };

  // Handle edit click
  const editVersion = (version) => {
    openVersionModal(version);
  };

  // Confirm delete modal open
  const confirmDelete = (version) => {
    setSelectedVersion(version);
    setShowConfirm(true);
  };

  // Delete handler
  const handleDelete = async () => {
    try {
      await deleteVersion(login.token, selectedVersion._id);
      toast.success("Version deleted");
      setShowConfirm(false);
      fetchVersions();
    } catch (error) {
      console.error("Error deleting version:", error);
      toast.error("Failed to delete version");
    }
  };

  // Handle form inputs change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setForm({ ...form, [name]: files[0] });
    }
  };

  // Save version
  const handleSubmit = async () => {
    try {
      const payload = new FormData();
      payload.append("version", form.version);
      payload.append("type", form.type);
      payload.append("warnDate", form.warnDate);
      payload.append("expiryDate", form.expiryDate);

      if (form._id) payload.append("_id", form._id);

      if (form.windowsFile instanceof File) payload.append("windowsFile", form.windowsFile);
      if (form.linuxFile instanceof File) payload.append("linuxFile", form.linuxFile);
      if (form.macFile instanceof File) payload.append("macFile", form.macFile);

      setUploadProgress(0);

      const res = await createOrUpdateVersion(login.token, payload, {
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percent);
        }
      });      

      if (res.success) {
        toast.success(res.message || "Saved successfully");
        fetchVersions();
        setShowModal(false);
        setUploadProgress(0);
      } else {
        toast.error(res.error || "Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save version");
      setUploadProgress(0);
    }
  };


  return (
    <div className="details-table mt-3 p-4 bg-white rounded shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="d-flex align-items-center">
          <i class="fa-solid fa-code-branch"></i> &nbsp;Version Manager
        </h4>
        <i class="fa-solid fa-code-branch" onClick={() => { openVersionModal() }} style={{ fontSize: "25px" }}></i>
      </div>

      <table className="table table-striped table-bordered">
        <thead className="table-dark">
          <tr>
            <th style={{ width: "5%" }}>No</th>
            <th style={{ width: "15%" }}>Version</th>
            <th>Type</th>
            <th style={{ width: "10%" }}>Warn Date</th>
            <th style={{ width: "10%" }}>Expiry Date</th>
            <th style={{ width: "20%" }}>Files</th>
            <th style={{ width: "20%" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {paginatedVersions.length > 0 ? (
            paginatedVersions.map((x, i) => (
              <tr key={x._id}>
                <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                <td>{x.version}</td>
                <td>{x.type || "-"}</td>
                <td>{x.warnDate ? moment(x.warnDate).format("YYYY-MM-DD") : "-"}</td>
                <td>{x.expiryDate ? moment(x.expiryDate).format("YYYY-MM-DD") : "-"}</td>
                <td>
                  {x.windowsFile && <a href={`${CONFIG.REACT_APP_UPLOADS}/${x.windowsFile}`} target="_blank" rel="noreferrer" title="Windows" className="me-2 text-primary"><i className="fab fa-windows fa-lg"></i></a>}
                  {x.linuxFile && <a href={`${CONFIG.REACT_APP_UPLOADS}/${x.linuxFile}`} target="_blank" rel="noreferrer" title="Linux" className="me-2 text-warning"><i className="fab fa-linux fa-lg"></i></a>}
                  {x.macFile && <a href={`${CONFIG.REACT_APP_UPLOADS}/${x.macFile}`} target="_blank" rel="noreferrer" title="Mac" className="text-secondary"><i className="fab fa-apple fa-lg"></i></a>}
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-info me-2"
                    style={{ backgroundColor: '#1edd60', border: "none", color: "#fff" }}
                    onClick={() => editVersion(x)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => confirmDelete(x)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center">
                No versions found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <span>
          Showing{" "}
          {versions.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} -{" "}
          {Math.min(currentPage * itemsPerPage, versions.length)} of {versions.length}
        </span>
        <div>
          <button
            className="page-btn prev"
            onClick={goPrev}
            disabled={isFirstPage} // Disable button on first page
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          {currentPage} / {totalPages || 1}
          <button
            className="page-btn next"
            onClick={goNext}
            disabled={isLastPage} // Disable button on last page
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{form._id ? "Edit Version" : "Add Version"}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Version</label>
                  <input type="text" name="version" value={form.version} onChange={handleChange} className="form-control" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <select name="type" value={form.type} onChange={handleChange} className="form-control">
                    <option value="">Select Type</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Warn Date</label>
                  <input type="date" name="warnDate" value={form.warnDate} onChange={handleChange} className="form-control" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Expiry Date</label>
                  <input type="date" name="expiryDate" value={form.expiryDate} onChange={handleChange} className="form-control" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Windows File (Exe)</label>
                  <input type="file" name="windowsFile" onChange={handleFileChange} className="form-control" />
                  {form.windowsFile && typeof form.windowsFile === 'string' && (
                    <small>Current: <a href={`${CONFIG.REACT_APP_UPLOADS}/${form.windowsFile}`} target="_blank" rel="noreferrer">Download</a></small>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Linux File</label>
                  <input type="file" name="linuxFile" onChange={handleFileChange} className="form-control" />
                  {form.linuxFile && typeof form.linuxFile === 'string' && (
                    <small>Current: <a href={`${CONFIG.REACT_APP_UPLOADS}/${form.linuxFile}`} target="_blank" rel="noreferrer">Download</a></small>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Mac File</label>
                  <input type="file" name="macFile" onChange={handleFileChange} className="form-control" />
                  {form.macFile && typeof form.macFile === 'string' && (
                    <small>Current: <a href={`${CONFIG.REACT_APP_UPLOADS}/${form.macFile}`} target="_blank" rel="noreferrer">Download</a></small>
                  )}
                </div>
                {uploadProgress > 0 && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>Uploading files</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="progress">
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{ width: `${uploadProgress}%` }}
                        role="progressbar"
                      />
                    </div>
                  </div>
                )}

              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit} style={{ backgroundColor: '#1edd60', border: "none", color: "#fff" }}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showConfirm && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirm(false)}
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete version "{selectedVersion?.version}"?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};